import * as React from "react";
import {
    AllPayrollPeriod,
    Contract,
    createPayrollPeriod,
    createSnapshot,
    getAllPayrollPeriods,
    getContractsByPeriodKey,
    getPayrollPeriods,
    PayrollPeriod as PayrollPeriodType,
} from "../services/payrollService";
import {useTranslation} from "react-i18next";
import {useAuth} from "../context/AuthContext";
import {usePeriod} from "../context/PeriodContext";
import {API_BASE_URL} from "../config/apiConfig";
import {getWorkEvidenceSummary} from "../services/workEvidenceService";

// ─── Wizard step helpers ─────────────────────────────────────────────────────
// Mirrors the derivation in useDashboard.ts (kept separate rather than shared,
// same tradeoff as the reconciliation cards in PayrollRun — see docs/PAYROLL_RUN_LAYOUT.md §5).
export type WizardStepKey = "contracts" | "evidence" | "calculation" | "approval" | "finalization";

export interface WizardStep {
    key: WizardStepKey;
    label: string;
    done: boolean;
}

// A work-evidence record is "pending" whenever it isn't approved yet (including records with no
// WorkEvidenceSummary row); the backend summary endpoint applies the same rule.

// ─── Sorting helpers ─────────────────────────────────────────────────────────
export type Order = "asc" | "desc";
export type PayrollPeriodSortableKey = "contractNumber" | "startDate" | "endDate" | "createdAt";
// Remove 'contactNumber' from ContractSortableKey as it does not exist on Contract type
export type ContractSortableKey =
    "firstName"
    | "lastName"
    | "payrollCalcType"
    | "salaryBasis"
    | "baseAmount"
    | "workload"
    | "startDate"
    | "endDate";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    const aVal = a[orderBy] ?? "";
    const bVal = b[orderBy] ?? "";
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function usePayrollPeriod() {
    const {token} = useAuth();
    const {t} = useTranslation();
    const {selectedPeriod, selectedPeriodValue, setPeriodAndValue, setSelectedPeriodValue, branches} = usePeriod();

    // Get current date (February 2026)
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

    const [selectedYear, setSelectedYear] = React.useState<number>(currentYear);
    const [selectedMonth, setSelectedMonth] =
        React.useState<number>(currentMonth);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [payrollPeriods, setPayrollPeriods] = React.useState<
        PayrollPeriodType[]
    >([]);
    const [totalRecords, setTotalRecords] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [allPeriods, setAllPeriods] = React.useState<AllPayrollPeriod[]>([]);
    const [creatingPeriod, setCreatingPeriod] = React.useState<boolean>(false);
    const [contracts, setContracts] = React.useState<Contract[]>([]);
    const [contractsLoading, setContractsLoading] =
        React.useState<boolean>(false);
    const [contractsPage, setContractsPage] = React.useState(0);
    const [contractsRowsPerPage, setContractsRowsPerPage] = React.useState(10);
    const [snapshotCreating, setSnapshotCreating] =
        React.useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] =
        React.useState<boolean>(false);
    const [successMessageText, setSuccessMessageText] = React.useState<string>(
        t('payroll.period.snapshotGeneratedSuccess')
    );
    const [snapshotError, setSnapshotError] = React.useState<string | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] =
        React.useState<boolean>(false);
    const [currentAction, setCurrentAction] = React.useState<
        "lock" | "unlock" | null
    >(null);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] =
        React.useState<boolean>(false);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

    // ─── Period status panel: evidence + calculation signals for the wizard/checklist ───────────
    const [evidenceTotal, setEvidenceTotal] = React.useState<number>(0);
    const [evidencePending, setEvidencePending] = React.useState<number>(0);
    const [evidenceLoading, setEvidenceLoading] = React.useState<boolean>(false);
    const [contractsLastSyncedAt, setContractsLastSyncedAt] = React.useState<Date | null>(null);

    // Sorting state for payroll periods
    const [periodOrder, setPeriodOrder] = React.useState<Order>("asc");
    const [periodOrderBy, setPeriodOrderBy] = React.useState<PayrollPeriodSortableKey>("contractNumber");
    const handlePeriodRequestSort = (property: PayrollPeriodSortableKey) => {
        const isAsc = periodOrderBy === property && periodOrder === "asc";
        setPeriodOrder(isAsc ? "desc" : "asc");
        setPeriodOrderBy(property);
    };
    const sortedPayrollPeriods = React.useMemo(
        () => [...payrollPeriods].sort(getComparator(periodOrder, periodOrderBy)),
        [payrollPeriods, periodOrder, periodOrderBy]
    );

    // Sorting state for contracts
    const [contractOrder, setContractOrder] = React.useState<Order>("asc");
    const [contractOrderBy, setContractOrderBy] = React.useState<ContractSortableKey>("firstName");
    const handleContractRequestSort = (property: ContractSortableKey) => {
        const isAsc = contractOrderBy === property && contractOrder === "asc";
        setContractOrder(isAsc ? "desc" : "asc");
        setContractOrderBy(property);
    };
    const sortedContracts = React.useMemo(
        () => [...contracts].sort(getComparator(contractOrder, contractOrderBy)),
        [contracts, contractOrder, contractOrderBy]
    );

    // Fetch all payroll periods
    const years = Array.from({length: 27}, (_, i) => 2000 + i);

    // Fetch all payroll periods for the dropdown on component mount
    React.useEffect(() => {
        const fetchAllPeriods = async () => {
            if (!token) {
                console.error("No authentication token available");
                return;
            }

            try {
                const periodsResponse = await getAllPayrollPeriods(token);
                setAllPeriods(periodsResponse);
            } catch (error) {
                console.error("Failed to fetch all payroll periods:", error);
                setAllPeriods([]);
            }
        };

        fetchAllPeriods();
    }, [token]);

    // Auto-fetch contracts if a period is already selected in context (restored from session)
    React.useEffect(() => {
        if (token && selectedPeriod) {
            setContractsLoading(true);
            getContractsByPeriodKey(token, selectedPeriod)
                .then((response) => {
                    setContracts(response.contracts);
                    setContractsPage(0);
                    setContractsLastSyncedAt(new Date());
                })
                .catch((error) => {
                    console.error("Failed to fetch contracts on mount:", error);
                    setContracts([]);
                })
                .finally(() => setContractsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Evidence + calculation status for the selected period's wizard/checklist (right-hand panel).
    React.useEffect(() => {
        if (!token || !selectedPeriod || branches.length === 0) {
            setEvidenceTotal(0);
            setEvidencePending(0);
            return;
        }
        let cancelled = false;
        setEvidenceLoading(true);
        getWorkEvidenceSummary(token, selectedPeriod).then((rows) => {
            if (cancelled) return;
            setEvidenceTotal(rows.reduce((sum, r) => sum + r.total, 0));
            setEvidencePending(rows.reduce((sum, r) => sum + r.pending, 0));
        }).catch(() => {
            if (cancelled) return;
            setEvidenceTotal(0);
            setEvidencePending(0);
        }).finally(() => {
            if (!cancelled) setEvidenceLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [token, selectedPeriod, branches]);

    // Final calculation is only known once the period is finalized ("Check and finalize" on Payroll Run);
    // before that it depends on work evidence approval, so the run's availability is not a valid signal.
    const calculationAvailable = React.useMemo(
        () => allPeriods.find((p) => p.periodKey === selectedPeriod)?.workflowStage === "FINALIZED",
        [allPeriods, selectedPeriod]
    );

    // Fetch payroll periods when year or month changes
    React.useEffect(() => {
        const fetchPayrollPeriods = async () => {
            if (!token) {
                console.error("No authentication token available");
                return;
            }

            setLoading(true);
            try {
                const response = await getPayrollPeriods(
                    token,
                    selectedYear,
                    selectedMonth
                );
                setPayrollPeriods(response.payrollPeriods);
                setTotalRecords(response.totalRecords);
            } catch (error) {
                console.error("Failed to fetch payroll periods:", error);
                setPayrollPeriods([]);
                setTotalRecords(0);
            } finally {
                setLoading(false);
            }
        };

        fetchPayrollPeriods();
    }, [token, selectedYear, selectedMonth]);

    // Months array
    const months = [
        {value: 1, label: t('common.monthFull.1')},
        {value: 2, label: t('common.monthFull.2')},
        {value: 3, label: t('common.monthFull.3')},
        {value: 4, label: t('common.monthFull.4')},
        {value: 5, label: t('common.monthFull.5')},
        {value: 6, label: t('common.monthFull.6')},
        {value: 7, label: t('common.monthFull.7')},
        {value: 8, label: t('common.monthFull.8')},
        {value: 9, label: t('common.monthFull.9')},
        {value: 10, label: t('common.monthFull.10')},
        {value: 11, label: t('common.monthFull.11')},
        {value: 12, label: t('common.monthFull.12')},
    ];

    const handleYearChange = (event: any) => {
        setSelectedYear(event.target.value);
    };

    const handleMonthChange = (event: any) => {
        setSelectedMonth(event.target.value);
    };

    const selectPeriod = async (selectedPeriodKey: string, selectedPeriodVal: string) => {
        setPeriodAndValue(selectedPeriodKey, selectedPeriodVal);

        if (!token) {
            console.error("No authentication token available");
            return;
        }

        setContractsLoading(true);
        try {
            const response = await getContractsByPeriodKey(token, selectedPeriodKey);
            setContracts(response.contracts);
            setContractsPage(0); // Reset to first page
            setContractsLastSyncedAt(new Date());
        } catch (error) {
            console.error("Failed to fetch contracts:", error);
            setContracts([]);
        } finally {
            setContractsLoading(false);
        }
    };

    const handleRefreshContracts = async () => {
        if (!token || !selectedPeriod) return;
        setContractsLoading(true);
        try {
            const response = await getContractsByPeriodKey(token, selectedPeriod);
            setContracts(response.contracts);
            setContractsPage(0);
            setContractsLastSyncedAt(new Date());
        } catch (error) {
            console.error("Failed to refresh contracts:", error);
        } finally {
            setContractsLoading(false);
        }
    };

    const handlePeriodChange = (event: any) => {
        const selectedValue = JSON.parse(event.target.value);
        void selectPeriod(selectedValue.key, selectedValue.value);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleContractsChangePage = (event: unknown, newPage: number) => {
        setContractsPage(newPage);
    };

    const handleContractsChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setContractsRowsPerPage(Number.parseInt(event.target.value, 10));
        setContractsPage(0);
    };

    const handleGenerateSnapshot = async () => {
        if (!token) {
            console.error("No authentication token available");
            setSnapshotError(t('payroll.error.authTokenNotAvailable'));
            return;
        }

        if (!selectedPeriod) {
            setSnapshotError(t('payroll.period.error.selectPeriodBeforeSnapshot'));
            return;
        }

        if (contracts.length === 0) {
            setSnapshotError(t('payroll.period.error.noContractsForSnapshot'));
            return;
        }

        setSnapshotCreating(true);
        setSnapshotError(null);
        try {
            // Extract contractIds from the selected contracts
            const contractIds = contracts.map((contract) => contract.contractId);

            const response = await createSnapshot(token, selectedPeriod, contractIds);

            if (response.success) {
                setShowSuccessMessage(true);
                setSuccessMessageText(
                    t('payroll.period.snapshotGeneratedSuccess')
                );
                console.log("Snapshot created successfully");
            } else {
                setSnapshotError(response.message || t('payroll.period.error.generateSnapshotFailed'));
            }
        } catch (error) {
            console.error("Failed to generate snapshot:", error);
            setSnapshotError(t('payroll.period.error.generateSnapshotFailedRetry'));
        } finally {
            setSnapshotCreating(false);
        }
    };

    const handleLockUnlock = async () => {
        if (!token) {
            console.error("No authentication token available");
            setSnapshotError(t('payroll.error.authTokenNotAvailable'));
            return;
        }

        if (!selectedPeriod) {
            setSnapshotError(t('payroll.period.error.selectPeriodBeforeLock'));
            return;
        }

        try {
            // Determine the new status based on current state
            const newStatus = isOpenState ? "LOCKED" : "OPEN";

            const response = await fetch(
                `${API_BASE_URL}/api/snapshot/update-status?periodKey=${selectedPeriod}&status=${newStatus}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to update snapshot status: ${response.statusText}`
                );
            }

            const responseData = await response.json();

            if (responseData.success) {
                setShowSuccessMessage(true);
                setSuccessMessageText(
                    isOpenState ? t('payroll.period.lockedSuccess') : t('payroll.period.unlockedSuccess')
                );
                console.log(
                    `Period ${isOpenState ? "locked" : "unlocked"} successfully`
                );

                // Refresh the periods dropdown with updated data
                const periodsResponse = await getAllPayrollPeriods(token);
                setAllPeriods(periodsResponse);

                // Update the selected period value to reflect the new status
                const updatedPeriod = periodsResponse.find(
                    (p) => p.periodKey === selectedPeriod
                );
                if (updatedPeriod) {
                    setSelectedPeriodValue(updatedPeriod.value);
                }

                // Refresh the contracts table
                if (selectedPeriod) {
                    const contractsResponse = await getContractsByPeriodKey(
                        token,
                        selectedPeriod
                    );
                    setContracts(contractsResponse.contracts);
                }

                // Refresh the payroll periods table
                const payrollPeriodsResponse = await getPayrollPeriods(
                    token,
                    selectedYear,
                    selectedMonth
                );
                setPayrollPeriods(payrollPeriodsResponse.payrollPeriods);
                setTotalRecords(payrollPeriodsResponse.totalRecords);
            } else {
                setSnapshotError(t('payroll.period.error.updateStatusFailed'));
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : t('payroll.period.error.updateStatusFailed');
            console.error("Error updating period status:", error);
            setSnapshotError(errorMessage);
        }
    };

    const handleCreatePeriod = async () => {
        if (!token) {
            console.error("No authentication token available");
            return;
        }

        setCreatingPeriod(true);
        try {
            // Make POST request to create period (send month number directly)
            const createResponse = await createPayrollPeriod(
                token,
                selectedYear,
                selectedMonth
            );

            if (createResponse.success) {
                console.log("Payroll period created successfully");

                // Fetch all periods after successful creation
                const periodsResponse = await getAllPayrollPeriods(token);
                setAllPeriods(periodsResponse);
                console.log("All periods fetched:", periodsResponse);

                // Refresh the payroll periods table
                const payrollPeriodsResponse = await getPayrollPeriods(
                    token,
                    selectedYear,
                    selectedMonth
                );
                setPayrollPeriods(payrollPeriodsResponse.payrollPeriods);
                setTotalRecords(payrollPeriodsResponse.totalRecords);
            }
        } catch (error) {
            console.error("Failed to create payroll period:", error);
        } finally {
            setCreatingPeriod(false);
        }
    };

    const handleDeleteSnapshot = async () => {
        if (!token) {
            console.error("No authentication token available");
            return;
        }

        setIsDeleting(true);
        try {
            // Extract contractIds from the contracts array
            const contractIds = contracts.map((contract) => contract.contractId);

            const response = await fetch(`${API_BASE_URL}/api/snapshot/delete`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    periodKey: selectedPeriod,
                    contractIds: contractIds,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to delete snapshot: ${response.statusText}`);
            }

            const responseData = await response.json();

            if (responseData.success) {
                setShowSuccessMessage(true);
                setSuccessMessageText(t('payroll.period.snapshotDeletedSuccess'));
                console.log("Snapshot deleted successfully");

                // Refresh the periods dropdown
                const periodsResponse = await getAllPayrollPeriods(token);
                setAllPeriods(periodsResponse);

                // Clear selected period and value
                setPeriodAndValue(null, null);
            } else {
                setSnapshotError(t('payroll.period.error.deleteSnapshotFailed'));
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : t('payroll.period.error.deleteSnapshotFailed');
            console.error("Error deleting snapshot:", error);
            setSnapshotError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    // Determine lock button state based on selected period (without expensive lookup)
    const isLockButtonVisible = selectedPeriod !== null;
    const isLockedState = selectedPeriodValue?.includes("LOCKED") ?? false;
    const isOpenState = selectedPeriodValue?.includes("OPEN") ?? false;
    const lockButtonText = isOpenState ? t('payroll.period.lockButton') : t('payroll.period.unlockButton');
    const lockButtonColor = isOpenState ? "#ff9800" : undefined;

    // ─── Derived: wizard steps for the selected period's status card/checklist ──────────────────
    const contractsStepDone = contracts.length > 0;
    const evidenceStepDone = contractsStepDone && evidenceTotal > 0 && evidencePending === 0;
    const calculationStepDone = evidenceStepDone && calculationAvailable;
    const approvalStepDone = calculationStepDone && isLockedState;
    const finalizationStepDone = approvalStepDone;

    const wizardSteps: WizardStep[] = [
        {key: "contracts", label: t('dashboard.wizard.steps.contracts'), done: contractsStepDone},
        {key: "evidence", label: t('dashboard.wizard.steps.evidence'), done: evidenceStepDone},
        {key: "calculation", label: t('dashboard.wizard.steps.calculation'), done: calculationStepDone},
        {key: "approval", label: t('dashboard.wizard.steps.approval'), done: approvalStepDone},
        {key: "finalization", label: t('dashboard.wizard.steps.finalization'), done: finalizationStepDone},
    ];
    const wizardCurrentStepIndex = wizardSteps.findIndex((s) => !s.done);
    const activeStepIndex = wizardCurrentStepIndex === -1 ? wizardSteps.length - 1 : wizardCurrentStepIndex;
    const isWizardComplete = wizardCurrentStepIndex === -1;
    const wizardStatusLabel = isWizardComplete
        ? t('dashboard.wizard.status.completed')
        : t('dashboard.wizard.status.inProgress');

    return {
        branches,
        selectPeriod,
        handleRefreshContracts,
        evidenceTotal,
        evidencePending,
        evidenceLoading,
        calculationAvailable,
        contractsLastSyncedAt,
        wizardSteps,
        activeStepIndex,
        isWizardComplete,
        wizardStatusLabel,
        selectedPeriod,
        selectedPeriodValue,
        selectedYear,
        selectedMonth,
        page,
        rowsPerPage,
        payrollPeriods,
        totalRecords,
        loading,
        allPeriods,
        creatingPeriod,
        contracts,
        contractsLoading,
        contractsPage,
        contractsRowsPerPage,
        snapshotCreating,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        snapshotError,
        setSnapshotError,
        confirmDialogOpen,
        setConfirmDialogOpen,
        currentAction,
        setCurrentAction,
        deleteConfirmDialogOpen,
        setDeleteConfirmDialogOpen,
        isDeleting,
        periodOrder,
        periodOrderBy,
        handlePeriodRequestSort,
        sortedPayrollPeriods,
        contractOrder,
        contractOrderBy,
        handleContractRequestSort,
        sortedContracts,
        years,
        months,
        handleYearChange,
        handleMonthChange,
        handlePeriodChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleContractsChangePage,
        handleContractsChangeRowsPerPage,
        handleGenerateSnapshot,
        handleLockUnlock,
        handleCreatePeriod,
        handleDeleteSnapshot,
        isLockButtonVisible,
        isLockedState,
        isOpenState,
        lockButtonText,
        lockButtonColor,
    };
}
